import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { CheckTotalEntry } from 'Src/feature/check/CheckTotalEntry';
import { Check } from 'Src/feature/check/models';
import { CheckPanelDiscounts } from 'Src/feature/check-discount/CheckPanelDiscounts';
import { useCheckItemName } from 'Src/feature/check-item/useCheckItemName';
import { Divider } from 'Src/feature/divider/Divider';
import { CheckPanelGiftCards } from 'Src/feature/gift-card/CheckPanelGiftCards';

import styles from './FlipAndTipCheck.module.scss';

const sectionId = 'FlipAndTipCheck';

interface FlipAndTipCheck {
  className?: string;
  check: Check | undefined;
}

const FlipAndTipCheck: FC<FlipAndTipCheck> = ({ className, check }) => {
  const { t } = useTranslation();
  const { getCheckItemName } = useCheckItemName();

  return (
    <div
      data-testid={sectionId}
      className={classNames(className, styles.root)}
    >
      <Flex horizontal justifyContent="space-between">
        <Copy variant="h6" value={t('flipAndTip.labels.checkTitle')} />
      </Flex>

      <section className={styles.items}>
        {
          check?.items.map((checkItem, index) => {

            const name = getCheckItemName({ checkItem, menuItemName: checkItem.menuItem.combinedName });

            let groupedModifiers: Record<string, { name: string; price: number }[]> | undefined = undefined;
            if (checkItem.modifiers) {
              groupedModifiers = checkItem.modifiers.reduce((acc, modifier) => {
                let name = modifier.name || modifier.groupName;
                if (modifier.cmdName && modifier.cmdName.toLowerCase() !== 'add') {
                  if (modifier.type === 'prefix') {
                    name = `${modifier.cmdName} ${name}`;
                  } else {
                    name = `${name} ${modifier.cmdName}`;
                  }
                }
                const { price } = modifier;

                acc[name] = acc[name] ?? [];
                acc[name].push({ name, price });
                return acc;
              }, {} as Record<string, { name: string; price: number }[]>);
            }

            const { grossLessItemDiscount, gross, note } = checkItem;

            return (
              <React.Fragment key={index}>
                <div className={styles.item_grid}>
                  <Copy
                    variant="body"
                    weight="semi-bold"
                    optional
                    value={name}
                  />
                  <Copy
                    variant="body"
                    weight="regular"
                    align="right"
                    value={checkItem.quantity || 1}
                  />
                  <Copy align="right">
                    {grossLessItemDiscount !== undefined && (
                      <Trans
                        i18nKey="checkItem.price"
                        parent="div"
                        context={gross !== undefined && gross !== grossLessItemDiscount ? 'hasDiscount' : ''}
                        values={{
                          gross,
                          grossLessItemDiscount,
                        }}
                        components={{
                          gross: <Copy weight="semi-bold" strike color="textSecondary" />,
                          grossLessItemDiscount: <Copy weight="semi-bold" />,
                        }}
                      />
                    )}
                  </Copy>
                  {Object.entries(groupedModifiers ?? {}).map((gm, i) => (
                    <React.Fragment key={`${gm[0]}-${gm[1]?.[0].price}-${i}`}>
                      <Copy
                        variant="body"
                        size="sm"
                        className={styles.modifier_name}
                        // if modifier has duplicates, append xN, where N is the quantity, to the modifier's name
                        value={gm[1] && gm[1].length > 1 ? t('CheckItemButton.modifierNameWithCount', { count: gm[1].length, name: gm[0] }) : gm[0]}
                      />
                      <span /* column 2 */ />
                      {(gm[1] !== undefined && gm[1][0].price !== undefined)
                        ? (
                          <Copy
                            variant="body"
                            size="sm"
                            align="right"
                            value={t('labels.util.currency', { value: gm[1][0]?.price * gm[1].length })}
                          />
                        )
                        : <span />
                      }
                    </React.Fragment>
                  ))}

                  {/* fill all both columns*/}
                  {note && (
                    <Copy
                      className={styles.item_grid_fill}
                      variant="body"
                      size="sm"
                      color="textTertiary"
                      value={note}
                    />
                  )}
                </div>
                <Divider />
              </React.Fragment>
            );
          })
        }
        <CheckPanelDiscounts check={check} />
        <CheckPanelGiftCards check={check} canEdit={false} />
      </section>
      <footer className={styles.footer}>
        <div className={styles.totals}>
          <CheckTotalEntry type="subtotal" check={check} align={{ value: 'right' }} />
          <CheckTotalEntry type="fees" check={check} align={{ value: 'right' }} />
          <CheckTotalEntry type="gratuities" check={check} align={{ value: 'right' }} />
          <CheckTotalEntry type="discount" check={check} align={{ value: 'right' }} />
          <CheckTotalEntry type="tax" check={check} align={{ value: 'right' }} />
          <CheckTotalEntry type="total" check={check} align={{ value: 'right' }} />
        </div>
      </footer>
    </div>
  );
};

export { FlipAndTipCheck };
